import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Drawer,
  alpha,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  ExitToApp as ExitToAppIcon,
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  QrCode as QrCodeIcon,
  DisplaySettings as DisplaySettingsIcon,
  Palette as PaletteIcon,
  Storefront as StorefrontIcon,
  Print as PrintIcon,
  Home as HomeIcon,
  PieChart as PieChartIcon,
} from '@mui/icons-material';
import Logo from '../assets/logoCartAzulCrop.png';
import LogoBranca from '../assets/logoCartCrop.png';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import usePermissions from '../hooks/usePermissions';
import BrandLogo from './BrandLogo';
import { useBranding } from '../context/BrandingContext';

const MENU_CONFIG_KEY = 'senhasApp:configMenuOpen';

const Sidebar = ({ sidebarOpen, customTheme }) => {
  const defaultTheme = useTheme();
  const theme = customTheme || defaultTheme;
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useCustomTheme();
  const { signOut, user } = useAuth();
  const { filterMenuItems } = usePermissions();
  const { name, subtitle, sizing, textSizing, isTitleVisible, isSubtitleVisible } = useBranding();
  // Dimensões do logo da sidebar (personalizáveis por tema)
  // Usa tamanhos específicos para aberta/fechada quando disponíveis, senão cai para sidebar padrão
  const chosenSizing = sidebarOpen ? (isDarkMode ? sizing?.sidebarOpen?.dark : sizing?.sidebarOpen?.light)
                                   : (isDarkMode ? sizing?.sidebarClosed?.dark : sizing?.sidebarClosed?.light);
  const sidebarLogoSizing = chosenSizing || (isDarkMode ? sizing?.sidebar?.dark : sizing?.sidebar?.light) || {};
  const configuredWidth = typeof sidebarLogoSizing.width === 'number' ? sidebarLogoSizing.width : (sidebarOpen ? 60 : 45);
  const configuredHeight = typeof sidebarLogoSizing.height === 'number' ? sidebarLogoSizing.height : (sidebarOpen ? 60 : 45);
  const compactScale = 0.75;
  const boxWidth = sidebarOpen ? `${configuredWidth}px` : `${Math.max(28, Math.round(configuredWidth * compactScale))}px`;
  const boxHeight = sidebarOpen ? `${configuredHeight}px` : `${Math.max(28, Math.round(configuredHeight * compactScale))}px`;
  // Tamanhos de texto configuráveis
  const sidebarTextSizes = textSizing?.sidebar || {};
  const titlePx = sidebarTextSizes.titlePx || 18;
  const subtitlePx = sidebarTextSizes.subtitlePx || 10;
  
  // Carrega o estado inicial do localStorage ou define como falso se não existir
  const [configOpen, setConfigOpen] = useState(() => {
    try {
      const savedState = localStorage.getItem(MENU_CONFIG_KEY);
      return savedState ? JSON.parse(savedState) : false;
    } catch (error) {
      console.error('Erro ao carregar estado do menu:', error);
      return false;
    }
  });

  // Salva o estado do menu de configurações no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(MENU_CONFIG_KEY, JSON.stringify(configOpen));
    } catch (error) {
      console.error('Erro ao salvar estado do menu:', error);
    }
  }, [configOpen]);

  // Função para navegar para uma rota
  const handleNavigate = (route) => {
    navigate(route);
  };

  // Função para alternar o dropdown de configurações
  const handleConfigClick = () => {
    setConfigOpen(!configOpen);
  };

  // Função para fazer logout
  const handleLogout = () => {
    signOut();
    navigate('/login');
  };

  // Função para obter o texto do menu baseado no cargo do usuário
  const getMenuText = (defaultText, path) => {
    if (path === '/painel-recepcao') {
      return 'Senhas Emitidas';
    }
    return defaultText;
  };

  // Todos os itens de menu disponíveis
  const allMenuItems = [
    { text: 'Início', icon: <HomeIcon />, path: '/home' },
    { text: 'Chamar Senhas', icon: <DashboardIcon />, path: '/dashboard' },
    { 
      text: getMenuText('Painel Recepção', '/painel-recepcao'), 
      icon: <StorefrontIcon />, 
      path: '/painel-recepcao' 
    },
    { text: 'Tela de Senhas', icon: <DisplaySettingsIcon />, path: '/painel-chamada' },
    { text: 'Emitir Senha', icon: <AddIcon />, path: '/emitir-senha' },
    { text: 'Relatórios', icon: <PieChartIcon />, path: '/relatorios' },
  ];

  // Lista de itens da configuração que será filtrada com base nas permissões
  const allConfigItems = [
    { text: 'Usuários', icon: <PeopleIcon />, path: '/usuarios' },
    { text: 'Serviços', icon: <QrCodeIcon />, path: '/configuracoes/servicos-senhas' },
    { text: 'Configuração do Painel', icon: <DisplaySettingsIcon />, path: '/painel-configuracao' },
    { text: 'Branding', icon: <PaletteIcon />, path: '/branding' },
  ];

  // Filtra os itens de menu com base nas permissões do usuário
  const menuItems = filterMenuItems(allMenuItems);
  
  // Filtra os itens de configuração com base nas permissões do usuário
  const configItems = filterMenuItems(allConfigItems);
  
  // Verifica se há algum item de configuração para exibir após a filtragem
  const hasConfigItems = configItems.length > 0;

  // Verifica se a rota atual está relacionada a configurações
  const isConfigActive = location.pathname === '/usuarios' || 
                          location.pathname === '/configuracoes' ||
                          location.pathname === '/configuracoes/servicos-senhas' ||
                          location.pathname === '/painel-configuracao' ||
                          location.pathname === '/branding';

  return (
    <Drawer
      variant="permanent"
      open={sidebarOpen}
      sx={{
        width: sidebarOpen ? 290 : 80,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: sidebarOpen ? 290 : 80,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          overflowX: 'hidden',
        },
      }}
    >
      <Box sx={{ py: 0.5, px: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 0 }}>
        <Box sx={{ 
          transition: 'all 0.3s ease',
          width: boxWidth,
          height: boxHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Branding dinâmico com fallback */}
          <BrandLogo 
            slot={sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}
            variant={isDarkMode ? 'dark' : 'light'}
            alt="Logo"
            sx={{ width: '100%', height: '100%', objectFit: 'contain', transition: 'all 0.3s ease', transform: sidebarOpen ? 'scale(1)' : 'scale(0.8)' }}
          />
        </Box>
        <Box sx={{
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          height: sidebarOpen ? 'auto' : 0,
          opacity: sidebarOpen ? 1 : 0,
          mt: sidebarOpen ? 0.5 : 0
        }}>
          {isTitleVisible('sidebar') && name && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              fontSize: `${titlePx}px`, 
              letterSpacing: '0.01em', 
              mb: 0, 
              color: isDarkMode ? 'white' : '#323B72',
              whiteSpace: 'nowrap',
              lineHeight: 1
            }}
          >
            {name}
          </Typography>
          )}
          {isSubtitleVisible('sidebar') && subtitle && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500, 
              fontSize: `${subtitlePx}px`, 
              letterSpacing: '-0.03em', 
              color: isDarkMode ? 'white' : '#323B72',
              whiteSpace: 'nowrap',
              lineHeight: 1
            }}
          >
            {subtitle}
          </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ 
        borderColor: alpha(theme.palette.divider, 0.1),
      }} />

      <List sx={{ py: 2, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            sx={{ 
              cursor: 'pointer',
              px: sidebarOpen ? 3 : 0,
              py: sidebarOpen ? 1.2 : 0,
              borderRadius: sidebarOpen ? '0 24px 24px 0' : '50%', 
              mr: sidebarOpen ? 3 : 1,
              ml: sidebarOpen ? 0 : 1,
              mb: 1,
              height: sidebarOpen ? 'auto' : '45px',
              width: sidebarOpen ? 'auto' : '45px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&.Mui-selected': {
                bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.3 : 0.15),
                }
              },
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.1 : 0.05),
              }
            }}
          >
            <ListItemIcon sx={{ 
              cursor: 'pointer',
              minWidth: sidebarOpen ? 56 : 'auto',
              width: sidebarOpen ? 'auto' : '28px',
              height: sidebarOpen ? 'auto' : '28px',
              m: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: location.pathname === item.path ? 'primary.main' : isDarkMode ? 'white' : 'black',
              '& .MuiSvgIcon-root': {
                fontSize: sidebarOpen ? '1.6rem' : '1.45rem'
              }
            }}>
              {item.icon}
            </ListItemIcon>
            {sidebarOpen && <ListItemText 
              primary={item.text} 
              sx={{ 
                pr: 1,
                '& .MuiTypography-root': { 
                  fontSize: '0.95rem', 
                  fontWeight: 500,
                  color: location.pathname === item.path ? 'primary.main' : isDarkMode ? 'white' : 'black',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }} 
            />}
          </ListItem>
        ))}

        {hasConfigItems && (
          <>
            <ListItem
              button
              sx={{ 
                cursor: 'pointer',
                px: sidebarOpen ? 3 : 0,
                py: sidebarOpen ? 1.2 : 0,
                borderRadius: sidebarOpen ? '0 24px 24px 0' : '50%', 
                mr: sidebarOpen ? 3 : 1,
                ml: sidebarOpen ? 0 : 1,
                mb: configOpen && sidebarOpen ? 0 : 1,
                height: sidebarOpen ? 'auto' : '45px',
                width: sidebarOpen ? 'auto' : '45px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.1),
                  color: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.3 : 0.15),
                  }
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.1 : 0.05),
                }
              }}
              onClick={!sidebarOpen ? () => handleNavigate('/configuracoes') : undefined}
              selected={isConfigActive}
            >
              <ListItemIcon 
                sx={{ 
                  cursor: 'pointer',
                  minWidth: sidebarOpen ? 56 : 'auto',
                  width: sidebarOpen ? 'auto' : '28px',
                  height: sidebarOpen ? 'auto' : '28px',
                  m: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: isConfigActive ? 'primary.main' : isDarkMode ? 'white' : 'black', 
                  '& .MuiSvgIcon-root': {
                    fontSize: sidebarOpen ? '1.6rem' : '1.45rem'
                  }
                }}
                onClick={!sidebarOpen ? undefined : () => handleNavigate('/configuracoes')}
              >
                <SettingsIcon />
              </ListItemIcon>
              {sidebarOpen && (
                <>
                  <Box 
                    sx={{ 
                      flexGrow: 1, 
                      cursor: 'pointer', 
                      display: 'flex', 
                      alignItems: 'center'
                    }}
                    onClick={() => handleNavigate('/configuracoes')}
                  >
                    <ListItemText 
                      primary="Configurações" 
                      sx={{ 
                        pr: 1,
                        '& .MuiTypography-root': { 
                          fontSize: '0.95rem', 
                          fontWeight: 500,
                          color: isConfigActive ? 'primary.main' : isDarkMode ? 'white' : 'black',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }} 
                    />
                  </Box>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      ml: 1,
                      mr: 1,
                      p: 0.5,
                      borderRadius: '50%', 
                      cursor: 'pointer',
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleConfigClick();
                    }}
                  >
                    {configOpen ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </>
              )}
            </ListItem>
            
            {sidebarOpen && (
              <Collapse in={configOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {configItems.map((item) => (
                    <ListItem
                      button
                      key={item.text}
                      component={Link}
                      to={item.path}
                      selected={location.pathname === item.path}
                      sx={{ 
                        pl: 9, 
                        py: 1.2, 
                        display: 'flex',
                        mr: 3,
                        borderRadius: '0 24px 24px 0',
                        '&.Mui-selected': {
                          bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.2 : 0.1),
                          color: theme.palette.primary.main,
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.3 : 0.15),
                          }
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, isDarkMode ? 0.1 : 0.05),
                        }
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          minWidth: 36, 
                          color: location.pathname === item.path ? 'primary.main' : isDarkMode ? 'white' : 'black',
                          '& .MuiSvgIcon-root': {
                            fontSize: '1.3rem'
                          }
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text} 
                        sx={{ 
                          pr: 1,
                          '& .MuiTypography-root': { 
                            fontSize: '0.9rem', 
                            fontWeight: 500,
                            color: location.pathname === item.path ? 'primary.main' : isDarkMode ? 'white' : 'black',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </>
        )}
        
        <Box sx={{ mt: 'auto' }}>
          <ListItem 
            button 
            onClick={handleLogout}
            sx={{ 
              px: sidebarOpen ? 3 : 0,
              py: sidebarOpen ? 1.2 : 0,
              borderRadius: sidebarOpen ? '0 24px 24px 0' : '50%',
              mr: sidebarOpen ? 2 : 1,
              ml: sidebarOpen ? 0 : 1, 
              height: sidebarOpen ? 'auto' : '45px',
              width: sidebarOpen ? 'auto' : '45px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, isDarkMode ? 0.1 : 0.05),
              }
            }}
          >
            <ListItemIcon sx={{ 
              minWidth: sidebarOpen ? 56 : 'auto',
              width: sidebarOpen ? 'auto' : '28px',
              height: sidebarOpen ? 'auto' : '28px',
              m: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: isDarkMode ? 'white' : 'black',
              '& .MuiSvgIcon-root': {
                fontSize: sidebarOpen ? '1.6rem' : '1.45rem'
              }
            }}>
              <ExitToAppIcon sx={{ color: theme.palette.error.main }} />
            </ListItemIcon>
            {sidebarOpen && (
              <ListItemText 
                primary="Sair" 
                sx={{ 
                  '& .MuiTypography-root': { 
                    fontSize: '0.95rem', 
                    fontWeight: 500,
                    color: theme.palette.error.main
                  }
                }} 
              />
            )}
          </ListItem>
        </Box>
      </List>
    </Drawer>
  );
};

export default Sidebar; 